import { Card, Chip } from '../components/ui';
import type { DashboardModuleProps } from './registry';

const T = {
  en: { tomorrow: 'Tomorrow', updated: 'Updated', bbq: 'Good for a barbecue', hot: 'Hot — go easy on the oven', cold: 'Cold — something warming' },
  fr: { tomorrow: 'Demain', updated: 'Mis à jour', bbq: 'Idéal pour un barbecue', hot: 'Chaud — évitez le four', cold: 'Froid — quelque chose de réconfortant' },
} as const;

/**
 * Household context, not weather analysis (§4, §55). The point is the rain
 * window — "is it worth eating outside, and when will it rain?"
 */
export function WeatherModule({ today, language }: DashboardModuleProps) {
  const t = T[language];
  const weather = today?.weather;
  if (!weather || weather.unavailable) return null;

  const updated = new Date(weather.updatedAt).toLocaleTimeString(language === 'fr' ? 'fr-FR' : 'en-GB', {
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <Card>
      <div className="row" style={{ alignItems: 'flex-start', gap: 18 }}>
        <div style={{ flex: 1, minWidth: 180 }}>
          <p className="dim">{weather.location}</p>
          <div className="row" style={{ gap: 12, alignItems: 'baseline' }}>
            <span style={{ fontSize: 40, fontWeight: 650, letterSpacing: '-0.02em' }}>
              {weather.current.temperature}°
            </span>
            <span style={{ fontSize: 19 }}>{weather.current.description}</span>
          </div>
          <p className="dim">
            {weather.today.high}° / {weather.today.low}°
          </p>
        </div>

        <div style={{ flex: 1, minWidth: 180 }}>
          {weather.rainSummary ? (
            <p style={{ fontWeight: 600, color: 'var(--fish)', marginBottom: 8 }}>{weather.rainSummary}</p>
          ) : (
            <p className="dim" style={{ marginBottom: 8 }}>
              {language === 'fr' ? 'Pas de pluie attendue' : 'No rain expected'}
            </p>
          )}

          {weather.tomorrow && (
            <p className="dim">
              <strong>{t.tomorrow}:</strong> {weather.tomorrow.high}° · {weather.tomorrow.description}
            </p>
          )}

          <div className="row" style={{ gap: 6, marginTop: 10 }}>
            {weather.hints.bbqFriendly && <Chip kind="accent">{t.bbq}</Chip>}
            {weather.hints.hotWeather && <Chip kind="fav">{t.hot}</Chip>}
            {weather.hints.coldWeather && <Chip kind="fish">{t.cold}</Chip>}
          </div>
        </div>
      </div>

      <p className="dim" style={{ marginTop: 12, fontSize: 13 }}>
        {t.updated} {updated} · {weather.source}
      </p>
    </Card>
  );
}
